import { Link } from '@tanstack/react-router';
import { ErroreApi } from '../api/cliente.js';
import { useState } from 'react';
import {
  useCancellaVista,
  useSalvaVista,
  useViste,
} from '../api/anagrafiche.js';
import {
  RICERCA_ORDINI,
  type RigaOrdine,
  useConteggiStato,
  useDefinizioneFiltriOrdini,
  useOrdini,
} from '../api/ordini.js';
import { type AzioneExtra, AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri, valoriMultipli } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { data, importo, numero, pastigliaPagamento } from '../formati.js';
import { statoOrdine, t } from '../i18n/lingua.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleEsportaOrdini } from './ModaleEsportaOrdini.js';
import { ModaleNuovoOrdine } from './ModaleNuovoOrdine.js';

/**
 * L'elenco degli ordini.
 *
 * È la pagina più guardata del programma. Rispetto al v2 aggiunge due cose che
 * lì mancano e che l'ufficio export si costruisce a mano ogni giorno: la riga
 * di stati con i numeri — quanti ordini ho in ogni fase, senza fare sette
 * ricerche — e la colonna dell'arrivo che dice se la data è certa o è una
 * stima del vettore, segnando in rosso quelli che sfondano la consegna
 * richiesta dal cliente.
 */

/**
 * Le due azioni che ha solo questo elenco, come nel v2.
 *
 * **Invia al vettore** manda i dati a C&L per il DAU e poi al vettore
 * dell'ordine, se è un altro. Chiede conferma perché i dati escono di qui e
 * vanno a qualcun altro: è l'unica azione di massa da cui non si torna
 * indietro con un clic.
 *
 * **Stato 2** porta all'elaborato gli ordini che stanno al ricevuto. Nel v2
 * si ferma tutto se anche uno solo non è allo stato 1; qui gli altri passano
 * e quello torna indietro col motivo.
 */
const MASSIMO_INVII = 100;

const AZIONI_ORDINI: AzioneExtra[] = [
  {
    etichetta: t('Invia al vettore'),
    percorso: '/api/orders/bulk/send-to-carrier',
    conferma: t('Mando al vettore gli ordini selezionati?'),
    opzione: {
      campo: 'force',
      etichetta: t('manda anche quelli che non sono allo stato 3'),
    },
    massimo: MASSIMO_INVII,
    esito: (n) => `${n.toLocaleString('it-IT')} ${n === 1 ? 'ordine mandato' : 'ordini mandati'}.`,
  },
  {
    etichetta: t('Stato 2'),
    percorso: '/api/orders/bulk/advance-to-state-2',
    conferma: t('Porto allo stato 2 gli ordini selezionati che sono allo stato 1?'),
    esito: (n) =>
      `${n.toLocaleString('it-IT')} ${n === 1 ? 'ordine portato' : 'ordini portati'} allo stato 2.`,
  },
];

const COLONNE: Colonna<RigaOrdine>[] = [
  {
    chiave: 'orderNumber',
    titolo: t('Ordine'),
    ordinabile: true,
    cella: (r) => (
      <>
        <Link className="cell-link strong" to="/sales-orders/$id" params={{ id: String(r.id) }}>
          {r.orderNumber}
        </Link>
        <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
          {r.sectional} {r.protocolNumber}
        </span>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviato')}
          </span>
        )}
      </>
    ),
  },
  { chiave: 'orderDate', titolo: t('Data'), ordinabile: true, cella: (r) => data(r.orderDate) },
  {
    chiave: 'customer',
    titolo: t('Cliente'),
    ordinabile: true,
    larghezzaMax: 240,
    cella: (r) => (
      <Link className="cell-link" to="/customers/$id" params={{ id: String(r.customerId) }}>
        {r.customer}
      </Link>
    ),
  },
  {
    chiave: 'destination',
    mobile: 'fuori',
    titolo: t('Destinazione'),
    larghezzaMax: 190,
    /**
     * Nella grande maggioranza degli ordini la destinazione si chiama come il
     * cliente, e ripeterla sprecherebbe la colonna più larga della tabella per
     * non dire niente. Quindi si mostra il paese — che per un ordine export è
     * il dato — e il nome solo quando la merce va altrove.
     */
    cella: (r) => {
      const uguale =
        r.destination && r.destination.trim().toUpperCase() === r.customer.trim().toUpperCase();
      return (
        <>
          {r.destinationCountry ?? <span className="dim">—</span>}
          {r.destination && !uguale && (
            <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
              {r.destination}
            </span>
          )}
        </>
      );
    },
  },
  {
    chiave: 'state',
    titolo: t('Stato'),
    ordinabile: true,
    // il colore è quello che l'azienda ha assegnato allo stato nel v2: chi
    // lavora lo riconosce già, non c'è motivo di inventarne un altro
    // il nome a database resta a portata di passaggio del mouse: è quello che
    // si dice a Business Central e ai vettori
    cella: (r) => (
      <span className="state-pill" title={r.stateOriginal}>
        <i
          className="state-num"
          style={r.stateColor ? ({ '--state': `#${r.stateColor}` } as never) : undefined}
        >
          {r.stateNumber}
        </i>
        {statoOrdine(r.state, r.stateOriginal).replace(/^\d+\.\s*/, '')}
      </span>
    ),
  },
  {
    chiave: 'vessel',
    titolo: t('Nave / container'),
    // quasi nessun ordine ha il nome della nave: quando manca, il container
    // sta da solo — un trattino davanti farebbe solo sembrare rotta la riga
    cella: (r) =>
      r.vessel || r.container ? (
        <span className="mono" style={{ fontSize: 11 }}>
          {r.vessel}
          {r.container && (
            <span className={r.vessel ? 'dim' : undefined} style={r.vessel ? { marginLeft: 6 } : undefined}>
              {r.container}
            </span>
          )}
        </span>
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'arrival',
    mobile: 'fuori',
    titolo: t('Arrivo'),
    ordinabile: true,
    // la data stimata dal vettore si scrive in corsivo: non è ancora un fatto
    cella: (r) =>
      r.arrival ? (
        <span
          className={r.late ? 'when late' : r.arrivalIsEstimate ? 'when eta' : 'when'}
          title={
            r.late
              ? t('Il cliente la chiedeva per il {data}', { data: data(r.deliveryDateRequested) })
              : r.arrivalIsEstimate
                ? t('Stima del vettore')
                : t('Arrivo confermato')
          }
        >
          {data(r.arrival)}
        </span>
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'total',
    titolo: t('Totale'),
    ordinabile: true,
    allineamento: 'destra',
    cella: (r) => importo(r.total, r.currency),
  },
  {
    chiave: 'paymentStatus',
    titolo: t('Pagamento'),
    ordinabile: true,
    cella: (r) => {
      const p = pastigliaPagamento(r.paymentStatus);
      return <span className={`badge ${p.classe}`}>{p.testo}</span>;
    },
  },
];

export function Ordini() {
  const { ricerca, testo, setTesto, incolla, cambia, vaiA, ordinaPer, filtra, applica } =
    useRicerca(RICERCA_ORDINI);
  const [nuovoAperto, setNuovoAperto] = useState(false);
  // il pulsante premuto, Excel o CSV: apre la finestra che chiede cosa esportare
  const [esporta, setEsporta] = useState<'xlsx' | 'csv' | null>(null);
  const [nomeVista, setNomeVista] = useState('');
  const [salvataggioAperto, setSalvataggioAperto] = useState(false);

  const elenco = useOrdini(ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const conteggi = useConteggiStato(ricerca);
  const definizioni = useDefinizioneFiltriOrdini();
  const viste = useViste('orders');
  const salvaVista = useSalvaVista('orders');
  const cancellaVista = useCancellaVista('orders');

  const dati = elenco.data;

  /**
   * Gli stati scelti stanno nell'indirizzo come `stateId=3,4,5`. Un clic
   * aggiunge o toglie: la domanda vera non è quasi mai «quali sono in
   * produzione» ma «quali non sono ancora partiti», che sono tre stati.
   */
  const statiScelti = valoriMultipli(ricerca.filters?.stateId);
  const cambiaStato = (id: number) => {
    const chiave = String(id);
    const nuovi = statiScelti.includes(chiave)
      ? statiScelti.filter((s) => s !== chiave)
      : [...statiScelti, chiave];
    filtra('stateId', nuovi.length ? nuovi.join(',') : undefined);
  };

  const salva = () => {
    const nome = nomeVista.trim();
    if (!nome) return;
    salvaVista.mutate(
      { name: nome, definition: { ...ricerca, page: 1 } },
      {
        onSuccess: () => {
          setNomeVista('');
          setSalvataggioAperto(false);
        },
      },
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Ordini')}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} ordini, archiviati compresi', { n: numero(dati.total) })
                : t('{n} ordini', { n: numero(dati.total) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => setEsporta('csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => setEsporta('xlsx')}>
            Excel
          </button>
          <Link className="btn" to="/sales-orders/import">
            {t('Importa da Excel')}
          </Link>
          <button className="btn btn-primary" onClick={() => setNuovoAperto(true)}>
            {t('Nuovo ordine')}
          </button>
        </div>
      </div>

      {/**
       * La riga degli stati con i numeri. Nel v2 per sapere quanti ordini sono
       * pronti per la spedizione si fa una ricerca, si legge il totale, si
       * cambia stato e si rifà: qui è una riga sola, e ogni numero è anche il
       * filtro per arrivarci.
       */}
      <div className="statebar">
        <button
          className={statiScelti.length === 0 ? 'state-tab state-tab-on' : 'state-tab'}
          onClick={() => filtra('stateId', undefined)}
        >
          {t('Tutti')}
          <b>{numero((conteggi.data?.counts ?? []).reduce((s, c) => s + c.n, 0))}</b>
        </button>
        {conteggi.data?.counts.map((c) => {
          const scelto = statiScelti.includes(String(c.stateId));
          // uno stato a riposo si vede finché dentro c'è qualcosa, o finché è
          // nel filtro: altrimenti non si potrebbe più togliere
          if (c.resting && c.n === 0 && !scelto) return null;
          return (
            <button
              key={c.stateId}
              className={scelto ? 'state-tab state-tab-on' : 'state-tab'}
              style={c.colorHex ? ({ '--state': `#${c.colorHex}` } as never) : undefined}
              onClick={() => cambiaStato(c.stateId)}
              aria-pressed={scelto}
              title={
                scelto
                  ? t('Togli «{stato}» dal filtro', { stato: c.state })
                  : t('{stato} — aggiungilo al filtro, si possono tenere più stati insieme', {
                      stato: c.stateOriginal,
                    })
              }
            >
              {/* il numero davanti al nome dello stato è già nel dato: si toglie */}
              {statoOrdine(c.state, c.stateOriginal).replace(/^\d+\.\s*/, '')}
              <b>{numero(c.n)}</b>
            </button>
          );
        })}
      </div>

      <div className="filterbar">
        <div className="presets">
          {viste.data?.views.map((vista) => (
            <span key={vista.id} className="preset-con-x">
              <button
                className="preset"
                onClick={() => applica(vista.definition)}
                title={vista.mine ? t('Vista tua') : t('Condivisa da un collega')}
              >
                {vista.name}
              </button>
              {vista.mine && (
                // un pulsante suo, accanto a quello della vista: dentro l'altro
                // pulsante, dalla tastiera non ci si arrivava
                <button
                  className="chip-x preset-x"
                  aria-label={t('Elimina la vista {nome}', { nome: vista.name })}
                  onClick={() => cancellaVista.mutate(vista.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {salvataggioAperto ? (
            <span className="row" style={{ gap: 4 }}>
              <input
                className="input"
                style={{ height: 26, width: 170 }}
                placeholder={t('Nome della vista')}
                // il servizio ne accetta 60: oltre, la vista non si salvava e niente lo diceva
                maxLength={60}
                value={nomeVista}
                autoFocus
                onChange={(e) => setNomeVista(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') salva();
                  if (e.key === 'Escape') setSalvataggioAperto(false);
                }}
              />
              <button className="btn" onClick={salva} disabled={salvaVista.isPending}>
                {t('Salva')}
              </button>
              {salvaVista.error instanceof ErroreApi && (
                <span className="hint" style={{ color: 'var(--tomato)' }}>
                  {salvaVista.error.message}
                </span>
              )}
            </span>
          ) : (
            <button
              className="preset btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => setSalvataggioAperto(true)}
            >
              {t('+ Salva questa vista')}
            </button>
          )}
        </div>

        <span className="omnisearch" style={{ maxWidth: 340, flex: '0 1 340px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per PO#, cliente, fattura, container')}
            title={t('Più PO# insieme: incollali separati da virgole, spazi o a capo')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            onPaste={incolla}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con gli archiviati') : t('Mostra archiviati')}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      {/* con trenta PO incollati e ventotto ordini a video, la domanda dopo è
          quali mancano: confrontarli a occhio è il lavoro da risparmiare */}
      {dati?.poList && (
        <div className="avviso-riga" role="status" style={{ marginTop: 0 }}>
          <span>
            {dati.poList.notFound.length === 0
              ? t('{n} PO# cercati: trovati tutti.', { n: numero(dati.poList.searched) })
              : t('{n} PO# cercati. Non trovati: {elenco}', {
                  n: numero(dati.poList.searched),
                  elenco: dati.poList.notFound.join(', '),
                })}
          </span>
        </div>
      )}

      <AzioniDiMassa
        percorso="/api/orders/bulk"
        nome="ordini"
        selezione={selezione}
        conElimina={definizioni.data?.canDelete ?? false}
        conRipristina={ricerca.archived}
        azioni={AZIONI_ORDINI}
        chiaviDaRileggere={['ordini', 'ordini-conteggi', 'conteggi-stato', 'avvisi']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q
              ? t('Nessun ordine per «{cercato}».', { cercato: ricerca.q })
              : t('Nessun ordine con questi filtri.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={t('ordini')}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovoAperto && <ModaleNuovoOrdine onChiudi={() => setNuovoAperto(false)} />}
      {esporta && (
        <ModaleEsportaOrdini
          ricerca={ricerca}
          formato={esporta}
          quanti={dati?.total}
          onChiudi={() => setEsporta(null)}
        />
      )}
    </>
  );
}
